import { useMemo } from 'react'
import {
  AXES,
  ROBOTS,
  hipHeight,
  legLength,
  ownMass,
  type DofKey,
  type PointKey,
  type SegmentKey,
} from '@/domain/hs1'
import { axes as rigAxes, rigFromModel } from '@/domain/rig'
import { Alert, Button, Select, Tabs } from '@/components/ui'
import { useSimulation } from '@/sim/SimulationProvider'
import { Metric, PageHead, num } from '@/sim/parts'
import { Axes } from './Axes'
import { Freedoms, Limits, Masses } from './Model'
import { Motion } from './Motion'
import { Features } from './Features'
import { Assumptions } from './Assumptions'

/**
 * Parameter — alles, was Eingabe ist, an einer Stelle.
 *
 * Hier lagen bis dahin drei Seiten: Modell (was die Maschine IST), Parameter
 * (was ein Mensch verstellt) und Merkmale (welche Modellanteile mitrechnen).
 * Die Trennung war eine des Rechenwegs und keine der Bedienung — wer eine
 * Knieachse verschiebt, will danach die Gangart wechseln und nicht die Seite.
 *
 * KEIN BÜHNENFENSTER. Das Strichmännchen ist hier das Bild, und es kann etwas,
 * was die Bühne nicht kann: man klickt hinein und bearbeitet, was man
 * angeklickt hat. Die Bühne steht auf den Seiten, die Bewegung zeigen.
 *
 * DER ÄNDERUNGSHINWEIS steht oben und nicht in einem Reiter. Eine Abweichung,
 * die man nur sieht, wenn man auf der richtigen Registerkarte steht, ist eine
 * Abweichung, die man übersieht — und alle Zahlen des Simulators rechnen mit
 * ihr weiter.
 */

/** Ein Änderungsschlüssel in Klartext. */
function editLabel(model: ReturnType<typeof useSimulation>['model'], key: string): string {
  const parts = key.split(':')
  if (parts[0] === 'point') {
    return `${model.points[parts[1] as PointKey]?.label ?? parts[1]} · ${parts[2]}`
  }
  if (parts[0] === 'mass') {
    const field = { kg: 'Masse', com: 'Schwerpunkt', gyration: 'Trägheitsradius' }[parts[2]]
    return `${model.masses[parts[1] as SegmentKey]?.label ?? parts[1]} · ${field ?? parts[2]}`
  }
  if (parts[0] === 'dof') {
    const axis = AXES.find((a) => a.key === parts[2])?.label ?? parts[2]
    const field = { lo: 'von', hi: 'bis', moment: 'Moment' }[parts[3]]
    return `${model.dofs[parts[1] as DofKey]?.label ?? parts[1]} · ${axis} · ${field ?? parts[3]}`
  }
  return key
}

/** Welcher Bereich betroffen ist — für die Zusammenfassung im Hinweis. */
function editArea(key: string): string {
  return { point: 'Gelenkpunkte', dof: 'Winkelfreiheiten', mass: 'Massen' }[key.split(':')[0]] ?? key
}

export function ParameterPage() {
  const { robot, base, model, edits, state, setRobot, setEdit, setOff, resetEdits, simulation } =
    useSimulation()
  const { mass } = simulation
  const stopped = Object.keys(state.off)

  const rig = useMemo(() => rigFromModel(model, state.off), [model, state.off])
  const axisList = useMemo(() => rigAxes(rig), [rig])

  const changed = Object.keys(edits)
  const areas = [...new Set(changed.map(editArea))]

  return (
    <div className="stack">
      <PageHead
        title="Parameter"
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            {changed.length > 0 && (
              <Button size="sm" variant="ghost" onClick={resetEdits}>
                Auf Grundstand
              </Button>
            )}
            <Select
              aria-label="Roboter"
              value={robot.id}
              onChange={(event) => setRobot(event.target.value)}
            >
              {Object.values(ROBOTS).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {/*
        DER HINWEIS AUF GEÄNDERTE GRUNDPARAMETER. Ohne ihn sieht man eine
        Abweichung nur im Feld selbst, und das setzt voraus, dass man auf der
        richtigen Registerkarte steht. Wer eine Achse verschoben hat und drei
        Seiten später eine überraschende Zahl sieht, muss hier nachschlagen
        können, was er selbst geändert hat.
      */}
      {changed.length > 0 && (
        <Alert
          tone="warning"
          title={`Grundparameter geändert — ${changed.length} Wert${changed.length > 1 ? 'e' : ''} in ${areas.join(', ')}`}
        >
          <div className="model__edits">
            {changed.map((key) => (
              <button
                key={key}
                type="button"
                className="model__edit"
                onClick={() => setEdit(key, null)}
                title="Zurücknehmen"
              >
                <span>{editLabel(base, key)}</span>
                <b className="numeric">{edits[key]}</b>
                <span className="model__edit-undo">zurück</span>
              </button>
            ))}
          </div>
          Der Grundstand von {robot.name} bleibt erhalten; jede Abweichung lässt sich einzeln
          zurücknehmen. Gerechnet wird ab hier mit den geänderten Werten — im Bild, in jeder
          Gangart, in der Antriebsliste, in der Stückliste und im Freigang.
        </Alert>
      )}

      {/*
        STILLGELEGTE ACHSEN. Sie gehören neben den Änderungshinweis und aus
        demselben Grund: sie wirken auf jede Zahl im Simulator, und wer sie
        vergessen hat, sucht den Fehler in der Rechnung.
      */}
      {stopped.length > 0 && (
        <Alert
          tone="warning"
          title={`${stopped.length} Achse${stopped.length > 1 ? 'n' : ''} stillgelegt`}
        >
          <div className="model__edits">
            {stopped.map((key) => (
              <button
                key={key}
                type="button"
                className="model__edit"
                onClick={() => setOff(key, null)}
                title="Wieder freigeben"
              >
                <span>{model.dofs[key as DofKey]?.label ?? key}</span>
                <b className="numeric">
                  {state.off[key].at}° · {state.off[key].drive ? 'Antrieb bleibt' : 'ohne Antrieb'}
                </b>
                <span className="model__edit-undo">frei</span>
              </button>
            ))}
          </div>
          Der Winkel steht fest, der Bodenkontakt rechnet danach neu. Wo der Antrieb ausgebaut ist,
          trägt die Struktur das Moment, und Masse wie Kosten fallen aus der Stückliste. Der Gang
          selbst wird nicht neu geplant — Kadenz und Schrittlänge bleiben, wie sie eingestellt sind.
        </Alert>
      )}

      <div className="metrics">
        <Metric
          label="Achsen"
          value={`${axisList.filter((a) => a.off === null).length}`}
          note={
            stopped.length === 0
              ? 'beide Körperseiten gezählt'
              : `${axisList.filter((a) => a.off !== null).length} stillgelegt`
          }
          warn={stopped.length > 0}
        />
        <Metric
          label="Eigenmasse"
          value={`${num(ownMass(model), 2)} kg`}
          note="Summe der Einzelmassen"
        />
        <Metric
          label="Abgleich"
          value={mass.delta === null ? '—' : `${mass.delta > 0 ? '+' : ''}${num(mass.delta, 2)} kg`}
          note={mass.target === null ? 'kein Vergleichswert' : `Ziel ${mass.target} kg`}
          warn={mass.relative !== null && Math.abs(mass.relative) > 0.02}
        />
        <Metric
          label="Beinlänge"
          value={`${num(1000 * legLength(model), 0)} mm`}
          note={`Hüfthöhe ${num(1000 * hipHeight(model), 0)} mm`}
        />
        <Metric
          label="Höchsttempo"
          value={`${num(model.performance.sprintMax, 1)} m/s`}
          note={`Gehen bis ${num(model.performance.walkMax, 1)}`}
        />
      </div>

      <Tabs
        aria-label="Parameter"
        items={[
          { id: 'axes', label: 'Achsbaum', content: <Axes /> },
          { id: 'dofs', label: 'Winkelfreiheiten', content: <Freedoms /> },
          { id: 'masses', label: 'Massen', content: <Masses /> },
          { id: 'motion', label: 'Bewegung', content: <Motion /> },
          { id: 'features', label: 'Merkmale', content: <Features /> },
          /* Die Annahmen lagen auf der Stückliste. Sie sind Eingaben und
             gehören zu den Eingaben — die Stückliste zeigt jetzt nur noch,
             was aus ihnen folgt. */
          { id: 'assumptions', label: 'Annahmen', content: <Assumptions /> },
          { id: 'limits', label: 'Abgeleitete Grenzen', content: <Limits /> },
        ]}
      />
    </div>
  )
}
